import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../utils/axiosInstance";
import ReactPaginate from "react-paginate";
import ReactModal from "react-modal";
import Select from "react-select";
import { toast } from "react-toastify";
import Fuse from "fuse.js";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";

const ReviewFeedback = () => {
    // --- State ---
    const [feedbacks, setFeedbacks] = useState([]);
    const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState({ value: "", label: "All Statuses" });
    const [sortOrder, setSortOrder] = useState("desc");
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [isOverwriteModalOpen, setIsOverwriteModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [newFeedbackText, setNewFeedbackText] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const feedbacksPerPage = 10;

    // --- Fetch feedback once ---
    useEffect(() => {
        (async () => {
            try {
                const res = await axiosInstance.get("/aichat/feedback");
                setFeedbacks(res.data);
            } catch (err) {
                console.error(err);
                toast.error("Failed to load feedback.");
            }
        })();
    }, []);

    // --- Fuse.js setup for search ---
    const fuse = useMemo(
        () =>
            new Fuse(feedbacks, {
                keys: ["question", "feedback"],
                threshold: 0.1,
                ignoreLocation: true,
            }),
        [feedbacks]
    );

    // --- Apply search, filter, sort ---
    useEffect(() => {
        let temp = searchTerm.trim()
            ? fuse.search(searchTerm).map((r) => r.item)
            : [...feedbacks];

        if (statusFilter.value) {
            temp = temp.filter((f) => f.status === statusFilter.value);
        }

        temp.sort((a, b) => {
            const da = new Date(a.created_at), db = new Date(b.created_at);
            return sortOrder === "asc" ? da - db : db - da;
        });

        setFilteredFeedbacks(temp);
        setCurrentPage(0);
    }, [feedbacks, searchTerm, statusFilter, sortOrder, fuse]);

    const pageCount = Math.ceil(filteredFeedbacks.length / feedbacksPerPage);
    const displayed = filteredFeedbacks.slice(
        currentPage * feedbacksPerPage,
        (currentPage + 1) * feedbacksPerPage
    );

    // --- Date formatter ---
    const fmt = (d) => {
        if (!d) return "-";
        const dt = new Date(d.replace(/ GMT$/, ""));
        const day = String(dt.getDate()).padStart(2, "0");
        const mon = dt.toLocaleString("en-AU", { month: "short" });
        const yr = dt.getFullYear();
        const hh = String(dt.getHours()).padStart(2, "0");
        const mm = String(dt.getMinutes()).padStart(2, "0");
        return `${day} ${mon}, ${yr} ${hh}:${mm}`;
    };

    // --- Approve / Reject ---
    const updateStatus = async (id, action) => {
        const newStatus = action === "approve" ? "approved" : "rejected";
        try {
            await axiosInstance.post(`/aichat/feedback/${id}/${action}`);
            toast.success(`Feedback ${newStatus}.`);
            setFeedbacks((fbs) =>
                fbs.map((f) =>
                    f.feedback_id === id ? { ...f, status: newStatus } : f
                )
            );
        } catch {
            toast.error(`Failed to ${action} feedback.`);
        }
    };

    // --- Overwrite handlers ---
    const openOverwrite = (item) => {
        setEditItem(item);
        setNewFeedbackText(item.feedback);
        setIsOverwriteModalOpen(true);
    };
    const submitOverwrite = async () => {
        setIsSaving(true);
        try {
            await axiosInstance.put(`/aichat/feedback/${editItem.feedback_id}`, {
                feedback: newFeedbackText,
            });
            toast.success("Feedback updated.");
            setFeedbacks((fbs) =>
                fbs.map((f) =>
                    f.feedback_id === editItem.feedback_id
                        ? { ...f, feedback: newFeedbackText }
                        : f
                )
            );
            setIsOverwriteModalOpen(false);
            setEditItem(null);
        } catch {
            toast.error("Failed to overwrite feedback.");
        } finally {
            setIsSaving(false);
        }
    };

    // --- Details handler ---
    const openDetails = (item) => {
        setDetailItem(item);
        setIsDetailsModalOpen(true);
    };

    // --- Status filter options ---
    const statusOptions = [
        { value: "", label: "All Statuses" },
        { value: "pending", label: "Pending" },
        { value: "approved", label: "Approved" },
        { value: "rejected", label: "Rejected" },
    ];

    // --- Download approved feedback as .docx ---
    const handleDownload = async () => {
        const approved = feedbacks.filter((f) => f.status === "approved");
        if (approved.length === 0) {
            toast.info("No approved feedback to download.");
            return;
        }
        const doc = new Document({
            sections: [
                {
                    children: approved.flatMap((item) => [
                        new Paragraph({
                            children: [
                                new TextRun({ text: "Question:", bold: true }),
                                new TextRun({ text: " " + item.question }),
                            ],
                            spacing: { after: 200 },
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({ text: "Feedback:", bold: true }),
                                new TextRun({ text: " " + item.feedback }),
                            ],
                            spacing: { after: 400 },
                        }),
                    ]),
                },
            ],
        });
        const blob = await Packer.toBlob(doc);
        const date = new Date().toLocaleString("en-AU", {
            timeZone: "Australia/Melbourne",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
        const formattedDate = date.replace(/\//g, "-");
        saveAs(blob, `approved_feedback_${formattedDate}.docx`);
        toast.success("Approved feedback downloaded.");
    };

    return (
        <div className="p-4 bg-gray-50 min-h-screen">
            {/* ← Back button */}
            <button
                onClick={() => window.history.back()}
                className="flex items-center px-5 py-2 mb-6 rounded-lg bg-teal-600 text-white font-semibold shadow-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-200 ease-in-out transform hover:scale-105"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 transform rotate-180"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path
                        fillRule="evenodd"
                        d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                        clipRule="evenodd"
                    />
                </svg>
                Back
            </button>

            <h1 className="text-4xl font-extrabold mb-6 text-gray-800 border-b pb-4">
                Review Feedback
            </h1>

            {/* Search, Filter, Sort, Download */}
            <div className="mb-6 flex flex-wrap items-center gap-4">
                <input
                    type="text"
                    placeholder="Search question or feedback..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-sm w-full border border-gray-300 p-2 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />

                <div className="max-w-xs w-full">
                    <Select
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={statusOptions}
                        classNamePrefix="react-select"
                        placeholder="Filter by Status"
                    />
                </div>

                <button
                    onClick={() => setSortOrder((o) => (o === "asc" ? "desc" : "asc"))}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md transition-all duration-200"
                >
                    Sort: {sortOrder === "asc" ? "↑ Oldest" : "↓ Newest"}
                </button>

                <button
                    onClick={handleDownload}
                    className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md duration-200 transition ease-in-out transform hover:scale-105"
                >
                    Download
                </button>
            </div>
            {/* Feedback list */}
            <div className="space-y-4">
                {displayed.length > 0 ? (
                    displayed.map((item) => {
                        const { feedback_id, question, feedback, status, created_at } = item;
                        const short = feedback.length > 60 ? feedback.slice(0, 60) + "..." : feedback;
                        return (
                            <div
                                key={feedback_id}
                                onClick={() => openDetails(item)}
                                className="bg-white p-4 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col md:flex-row justify-between cursor-pointer"
                            >
                                <div className="flex-1">
                                    <h2 className="text-lg font-semibold mb-1 truncate">{question}</h2>
                                    <p className="text-gray-700 mb-2">{short}</p>
                                    <p className="text-sm text-gray-500">
                                        Status: <span className="font-medium">{status}</span> | {fmt(created_at)}
                                    </p>
                                </div>
                                <div className="flex items-center justify-center space-x-3">
                                    {/* Approve */}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); updateStatus(item.feedback_id, "approve"); }}
                                        className="p-2 rounded-full bg-green-500 text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-opacity-75 transition duration-150 ease-in-out transform hover:scale-110"
                                        title="Approve"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </button>

                                    {/* Reject */}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); updateStatus(item.feedback_id, "reject"); }}
                                        className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75 transition duration-150 ease-in-out transform hover:scale-110"
                                        title="Reject"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>

                                    {/* Overwrite */}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); openOverwrite(item); }}
                                        className="p-2 rounded-full bg-yellow-500 text-white hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-75 transition duration-150 ease-in-out transform hover:scale-110"
                                        title="Overwrite"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-7.293 7.293A1 1 0 018.001 14H5a1 1 0 01-1-1v-3a1 1 0 01.293-.707l7.293-7.293zM14 6L11 9l-2-2 3-3 2 2z" />
                                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-gray-500 text-center py-8">No feedback found.</div>
                )}
            </div>

            {/* Pagination */}
            <ReactPaginate
                previousLabel={<span className="font-semibold text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-200 transition duration-200 ease-in-out">← Previous</span>}
                nextLabel={<span className="font-semibold text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-200 transition duration-200 ease-in-out">Next →</span>}
                pageCount={pageCount}
                onPageChange={({ selected }) => setCurrentPage(selected)}
                containerClassName="flex justify-center items-center mt-8 space-x-2 text-lg"
                pageClassName="block w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-blue-100 hover:border-blue-300 transition duration-200 ease-in-out"
                activeClassName="!bg-blue-600 !text-white !border-blue-600 shadow-md"
                disabledClassName="opacity-50 cursor-not-allowed"
                breakClassName="px-3 py-1.5 rounded-md text-gray-500"
                renderOnZeroPageCount={null}
            />

            {/* Details Modal */}
            <ReactModal
                isOpen={isDetailsModalOpen}
                onRequestClose={() => setIsDetailsModalOpen(false)}
                className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full max-h-[80vh] overflow-y-auto"
                overlayClassName="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center"
            >
                <h2 className="text-2xl font-semibold mb-4">Feedback Details</h2>
                {detailItem && (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Question</label>
                            <input
                                type="text"
                                readOnly
                                value={detailItem.question}
                                className="w-full border border-gray-300 p-2 rounded-md bg-gray-100 cursor-not-allowed"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Feedback</label>
                            <textarea
                                readOnly
                                rows={4}
                                value={detailItem.feedback}
                                className="w-full border border-gray-300 p-2 rounded-md bg-gray-100 cursor-not-allowed"
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={() => setIsDetailsModalOpen(false)}
                                className="py-2 px-4 bg-gray-300 hover:bg-gray-400 rounded-md transition-all duration-200"
                            >
                                Close
                            </button>
                        </div>
                    </>
                )}
            </ReactModal>

            {/* Overwrite Modal */}
            <ReactModal
                isOpen={isOverwriteModalOpen}
                onRequestClose={() => setIsOverwriteModalOpen(false)}
                className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full max-h-[80vh] overflow-y-auto"
                overlayClassName="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center"
            >
                <h2 className="text-2xl font-semibold mb-4">Overwrite Feedback</h2>
                {editItem && (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Question</label>
                            <input
                                type="text"
                                readOnly
                                value={editItem.question}
                                className="w-full border border-gray-300 p-2 rounded-md bg-gray-100 cursor-not-allowed"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Feedback</label>
                            <textarea
                                rows={4}
                                value={newFeedbackText}
                                onChange={(e) => setNewFeedbackText(e.target.value)}
                                className="w-full border border-gray-300 p-2 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => setIsOverwriteModalOpen(false)}
                                className="py-2 px-4 bg-gray-300 hover:bg-gray-400 rounded-md transition-all duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitOverwrite}
                                disabled={isSaving}
                                className={`py-2 px-4 rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all duration-200 ${isSaving
                                    ? "bg-gray-400 cursor-not-allowed text-white"
                                    : "bg-blue-600 hover:bg-blue-700 text-white"
                                    }`}
                            >
                                {isSaving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </>
                )}
            </ReactModal>
        </div>
    );
};

export default ReviewFeedback;
